import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { ensurePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { COUNTRY_OPTIONS } from '@/lib/constants/countryOptions';
import { AttachmentTemplateForm } from '../../../-components/AttachmentTemplateForm';
import { useAttachmentTemplateDocumentTypes } from '../../../-hooks/useAttachmentTemplateDocumentTypes';
import { useCreateAdminAttachmentTemplate } from '../../../-hooks/useCreateAdminAttachmentTemplate';
import type { AttachmentTemplateFormValues } from '../../../-types';
import { toTemplatePayload } from '../../../-utils/attachmentTemplateTransformers';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/create/',
)({
  // Hiding the Create button is not access control — this URL can be typed.
  beforeLoad: async () => {
    const perms = await ensurePermissions();
    if (!perms.orders.canCreate) {
      throw redirect({ to: '/orders/attachment-templates' });
    }
  },
  component: CreateAttachmentTemplatePage,
});

function CreateAttachmentTemplatePage() {
  const { getTranslation: t } = useTranslator({ page: 'attachmentTemplates' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useCreateAdminAttachmentTemplate();
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const countryOptions = COUNTRY_OPTIONS;
  const { data: documentTypeOptions = [] } =
    useAttachmentTemplateDocumentTypes();

  const allCountryCodes = countryOptions.map(c => c.key);
  // The backup rule's "all remaining" scope is derived from these at save
  // time; without them it would be persisted with no facility processes and
  // could never match an order.
  const allFpKeys = (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
    g.options.map(o => o.key),
  );

  const handleSubmit = async (values: AttachmentTemplateFormValues) => {
    try {
      const payload = toTemplatePayload(values, allCountryCodes, allFpKeys);
      const result = await mutation.mutateAsync(payload);
      toast.success(t('create.success'));
      await queryClient.invalidateQueries({
        queryKey: ['orders', 'adminAttachmentTemplates'],
      });
      void navigate({
        to: '/orders/attachment-templates/$id',
        params: { id: result.id },
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <AttachmentTemplateForm
      onSubmit={handleSubmit}
      isPending={mutation.isPending}
      submitLabel={t('create.title')}
      fpGroups={fpGroups as FacilityProcessGroupOption[]}
      countryOptions={countryOptions}
      documentTypeOptions={documentTypeOptions}
    />
  );
}
